import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { dhProfileService } from "@/entities/dh-profile/api/dhProfileService";
import { Button } from "@/shared/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/dialog";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { Textarea } from "@/shared/ui/textarea";
import { toast } from "@/shared/hooks/use-toast";
import {
  characterService,
  type CharacterDto,
  type CharacterUpsertRequest,
} from "@/entities/character/api/characterService";
import { errorToast } from "@/shared/lib/apiErrors";

/**
 * 人物基本信息**编辑**表单(packs W2;详情页「编辑信息」)。
 * 只剩三件真有消费面的:名字、外观一句话(subjects 文本锁逐字用)、声音绑定 ——
 * 角色卡 12 字段 0916 整块退役(见下方注释块)。
 *
 * <p>★0916 起它不再兼任「新建」入口:两类人物各自走一条建档流
 * (`/characters/new-real` / `/characters/new-virtual`),建的那一步只收名字与外观。
 * 所以 `character` 现在是**必传** —— 留一条谁都点不到的 create 分支,只会让下一个人
 * 以为还有第二个建人物的口。
 */
export function CharacterFormDialog({
  open,
  onOpenChange,
  character,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  character: CharacterDto;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [appearance, setAppearance] = useState("");
  // W5 声音绑定:none=跟随出片时选择 / preset=预置音色 / profile=我的声纹(与出片契约二选一同构)
  const [voiceMode, setVoiceMode] = useState<"none" | "preset" | "profile">("none");
  const [presetVoice, setPresetVoice] = useState("");
  const [voiceProfileId, setVoiceProfileId] = useState<number | null>(null);

  // 打开时按目标人物回填(编辑),或清空(新建)。依赖 open:同一个 dialog 复用于不同人物。
  useEffect(() => {
    if (!open) return;
    setName(character.name);
    setAppearance(character.appearance ?? "");
    if (character.voiceProfileId) {
      setVoiceMode("profile");
      setVoiceProfileId(character.voiceProfileId);
      setPresetVoice("");
    } else if (character.presetVoice) {
      setVoiceMode("preset");
      setPresetVoice(character.presetVoice);
      setVoiceProfileId(null);
    } else {
      setVoiceMode("none");
      setPresetVoice("");
      setVoiceProfileId(null);
    }
  }, [open, character]);

  const presetVoicesQuery = useQuery({
    queryKey: ["dh", "preset-voices"],
    queryFn: () => dhProfileService.listPresetVoices(),
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const voiceProfilesQuery = useQuery({
    queryKey: ["dh", "voice-profiles"],
    queryFn: () => dhProfileService.listVoiceProfiles(),
    enabled: open,
    staleTime: 60 * 1000,
  });
  const presetVoices = presetVoicesQuery.data ?? [];
  // 只列 active 的声纹(disabled=管理员强制下架,绑上去出片也用不了;契约 VoiceProfile.status)
  const voiceProfiles = (voiceProfilesQuery.data?.profiles ?? []).filter(
    (p) => p.status === "active"
  );

  const save = useMutation({
    mutationFn: (req: CharacterUpsertRequest) => characterService.update(character.id, req),
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ["characters"] });
      toast({ title: "已保存人物信息", description: `「${saved.name}」的基本信息已更新。` });
      onOpenChange(false);
    },
    onError: (e) => toast(errorToast(e, { fallbackTitle: "保存失败" })),
  });

  const submit = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast({ title: "名字必填", description: "先给人物起个名字。", variant: "destructive" });
      return;
    }
    if (voiceMode === "preset" && !presetVoice) {
      toast({
        title: "还没选音色",
        description: "选一个预置音色,或改回「跟随出片设置」。",
        variant: "destructive",
      });
      return;
    }
    if (voiceMode === "profile" && !voiceProfileId) {
      toast({
        title: "还没选声纹",
        description: "选一条我的声纹,或改回「跟随出片设置」。",
        variant: "destructive",
      });
      return;
    }
    save.mutate({
      name: trimmed,
      appearance: appearance.trim(),
      // 声音绑定二选一;update 语义 ""=清空 presetVoice、0=清空 voiceProfileId
      presetVoice: voiceMode === "preset" ? presetVoice : "",
      voiceProfileId: voiceMode === "profile" ? voiceProfileId : 0,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>编辑人物信息</DialogTitle>
          <DialogDescription>外观一句话会逐字进提示词链,改完对后续出片立即生效。</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="char-name">名字 *</Label>
            <Input
              id="char-name"
              value={name}
              maxLength={64}
              placeholder="如:老周"
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="char-appearance">外观一句话</Label>
            <Textarea
              id="char-appearance"
              value={appearance}
              maxLength={512}
              rows={2}
              placeholder="如:方脸,皱纹有故事感,眼神专注;黑白掺杂短发;藏蓝色工装服"
              onChange={(e) => setAppearance(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              出片时逐字锁进画面主体描述(形象一致性的文字锚),写得越具体越稳。
            </p>
          </div>

          <div className="space-y-1.5">
            <Label>声音</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              <Select
                value={voiceMode}
                onValueChange={(v) => {
                  setVoiceMode(v as typeof voiceMode);
                  if (v !== "preset") setPresetVoice("");
                  if (v !== "profile") setVoiceProfileId(null);
                }}
              >
                <SelectTrigger data-testid="voice-mode">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">跟随出片设置(不绑定)</SelectItem>
                  <SelectItem value="preset">预置音色</SelectItem>
                  <SelectItem value="profile">我的声纹</SelectItem>
                </SelectContent>
              </Select>
              {voiceMode === "preset" && (
                <Select value={presetVoice} onValueChange={setPresetVoice}>
                  <SelectTrigger data-testid="voice-preset">
                    <SelectValue placeholder="选一个音色" />
                  </SelectTrigger>
                  <SelectContent>
                    {presetVoices.map((v) => (
                      <SelectItem key={v.id} value={v.id}>
                        {v.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              {voiceMode === "profile" && (
                <Select
                  value={voiceProfileId ? String(voiceProfileId) : ""}
                  onValueChange={(v) => setVoiceProfileId(Number(v))}
                >
                  <SelectTrigger data-testid="voice-profile">
                    <SelectValue
                      placeholder={voiceProfiles.length === 0 ? "还没有可用声纹" : "选一条声纹"}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {voiceProfiles.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <p className="text-xs text-muted-foreground">
              绑定后,用这个人物出口播视频默认带这把声音;不绑定则出片时再选。声纹在 「AI 数字人 →
              我的声纹」录制。
            </p>
          </div>

          {/*
            ★「角色卡」12 字段表单退役(0916)。
            查证:`personaJson` **零消费面** —— realPersonCast 不带、entities 不带、
            VideoTaskCreateRequest 根本没这个字段;api 只有 CRUD,console 只把它显示回给填它的人。
            两台机器 10 个人物 **0 个填过**。它是一个"写进去只为了显示给自己看"的表单,
            而页面上还标着「进提示词链」—— 那句话不成立。
            真正进提示词链的是**外观一句话**(上面那个字段):提交编排时 withCharacterSheet
            把它逐字拼成【主角人物卡】。留它,删这 12 个。
            (漫剧线是独立仓、独立角色系统,只是个 externalUrl 跳出去,不共用本人物库 ——
             "留给漫剧用"这条路不成立。)
          */}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
            取消
          </Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? "保存中…" : "保存"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
